import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Combobox, CustomTable, ETypeOfNotification, InputDate, InputTextCustom, Modal, setNotification } from 'medere-ui-framework';
import {
    getAllFinancialAccounts,
    createFinancialAccount,
    updateFinancialAccount,
    getBalance,
    listMovements,
    createMovement,
    createBalanceCorrection,
    getMovementTypes,
    getAllPaymentMethods,
    getAccountPaymentMethods,
    associatePaymentMethod,
    disassociatePaymentMethod,
} from '../../services/FinancialAccountController';
import {
    FinancialAccountDTO,
    MovementResponseDTO,
    MovementTypeOption,
    PaymentMethodDTO,
} from '../../dto/FinancialAccountDTO';
import TransferModal, { TransferAccountOption } from '../../components/TransferModal';
import FieldError from '../../components/FieldError/FieldError';
import { getFieldErrorMessage, INSUFFICIENT_BALANCE, parseApiError } from '../../services/apiError';
import './FinancialAccountView.scss';


const CURRENCY_OPTIONS = [
    { label: 'ARS', value: 'ARS' },
    { label: 'USD', value: 'USD' },
];

const ACCOUNT_TYPE_LABELS: Record<number, string> = { 1: 'Bancaria', 2: 'Física' };

const formatMoney = (value: number | null | undefined) =>
    value == null ? '—' : `$ ${Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleString('es-AR') : '—';

// En una transferencia, el egreso muestra la cuenta destino y el ingreso la cuenta origen
const formatCounterpart = (row: MovementResponseDTO) => {
    if (!row.counterpartAccountName) return '—';
    return `${row.amount < 0 ? 'Hacia' : 'Desde'}: ${row.counterpartAccountName}`;
};

function dateParam(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
}

function startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

// ── Tipo para el combobox de cuentas (reutiliza TransferAccountOption) ──
type AccountOption = TransferAccountOption;

export default function FinancialAccountView() {
    const dispatch = useDispatch();

    // ── Tab state ──
    const [activeTab, setActiveTab] = useState<'accounts' | 'movements'>('accounts');

    // ── Cuentas ──
    const [accounts, setAccounts] = useState<FinancialAccountDTO[]>([]);
    const [accountBalances, setAccountBalances] = useState<Record<number, number>>({});
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [refreshAccounts, setRefreshAccounts] = useState(0);

    // ── Modal nueva cuenta ──
    const [newAccountModalVisible, setNewAccountModalVisible] = useState(false);
    const [newAccountName, setNewAccountName] = useState('');
    const [newAccountCurrency, setNewAccountCurrency] = useState('ARS');
    const [newAccountObservations, setNewAccountObservations] = useState('');
    const [newAccountType, setNewAccountType] = useState(2);
    const [newAccountActive, setNewAccountActive] = useState(true);
    const [newAccountPaymentMethods, setNewAccountPaymentMethods] = useState<PaymentMethodDTO[]>([]);
    const [creatingAccount, setCreatingAccount] = useState(false);

    // ── Movimientos ──
    const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);
    const [currentBalance, setCurrentBalance] = useState<number | null>(null);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [refreshMovements, setRefreshMovements] = useState(0);
    const [movFromDate, setMovFromDate] = useState<Date>(() => startOfDay(new Date()));
    const [movToDate, setMovToDate] = useState<Date>(() => new Date());

    // ── Modal nuevo movimiento ──
    const [movementTypes, setMovementTypes] = useState<MovementTypeOption[]>([]);
    const [movModalVisible, setMovModalVisible] = useState(false);
    const [movType, setMovType] = useState<MovementTypeOption | null>(null);
    const [movAmount, setMovAmount] = useState<number | null>(null);
    const [movDate, setMovDate] = useState<Date>(new Date());
    const [movNotes, setMovNotes] = useState('');
    const [creatingMovement, setCreatingMovement] = useState(false);
    const [movAmountError, setMovAmountError] = useState<string | null>(null);
    const movAmountRef = useRef<HTMLInputElement>(null);

    // ── Modal corrección de saldo ──
    const [balanceCorrectionVisible, setBalanceCorrectionVisible] = useState(false);
    const [correctionAccount, setCorrectionAccount] = useState<FinancialAccountDTO | null>(null);
    const [correctionCurrentBalance, setCorrectionCurrentBalance] = useState<number | null>(null);
    const [correctionTargetBalance, setCorrectionTargetBalance] = useState<number | null>(null);
    const [correctionDate, setCorrectionDate] = useState<Date>(new Date());
    const [correctionNotes, setCorrectionNotes] = useState('');
    const [correctionTargetError, setCorrectionTargetError] = useState<string | null>(null);
    const correctionTargetRef = useRef<HTMLInputElement>(null);
    const [loadingCorrectionBalance, setLoadingCorrectionBalance] = useState(false);
    const [correctingBalance, setCorrectingBalance] = useState(false);

    // ── Modal transferencia ──
    const [transferModalVisible, setTransferModalVisible] = useState(false);
    const [transferDefaultSource, setTransferDefaultSource] = useState<TransferAccountOption | null>(null);

    // ── Modal editar cuenta ──
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingAccount, setEditingAccount] = useState<FinancialAccountDTO | null>(null);
    const [editName, setEditName] = useState('');
    const [editCurrency, setEditCurrency] = useState('ARS');
    const [editObservations, setEditObservations] = useState('');
    const [editActive, setEditActive] = useState(true);
    const [savingAccount, setSavingAccount] = useState(false);

    // ── Medios de pago (dentro del modal de edición) ──
    const [allPaymentMethods, setAllPaymentMethods] = useState<PaymentMethodDTO[]>([]);
    const [accountPaymentMethods, setAccountPaymentMethods] = useState<PaymentMethodDTO[]>([]);
    const [loadingPMs, setLoadingPMs] = useState(false);

    // ── Cargar cuentas ──
    useEffect(() => {
        let cancelled = false;
        setLoadingAccounts(true);

        getMovementTypes()
            .then(types => {
                if (!cancelled) setMovementTypes(types);
            })
            .catch(console.error);

        getAllFinancialAccounts()
            .then((data: FinancialAccountDTO[]) => {
                if (cancelled) return;
                setAccounts(data);
                const options: AccountOption[] = data
                    .filter(a => a.active)
                    .map(a => ({
                        financialAccount: a.financialAccount,
                        name: a.name,
                        displayDescription: a.name,
                    }));
                setAccountOptions(options);

                // Cargar saldos de todas las cuentas
                data.forEach(a => {
                    getBalance(a.financialAccount)
                        .then(balance => {
                            if (cancelled) return;
                            setAccountBalances(prev => ({ ...prev, [a.financialAccount]: balance }));
                        })
                        .catch(() => { /* silenciar error de saldo individual */ });
                });
            })
            .catch(console.error)
            .finally(() => {
                if (!cancelled) setLoadingAccounts(false);
            });
        return () => { cancelled = true; };
    }, [refreshAccounts]);

    // ── Cargar saldo de la cuenta seleccionada ──
    useEffect(() => {
        if (!selectedAccount) {
            setCurrentBalance(null);
            return;
        }
        let cancelled = false;
        setLoadingBalance(true);
        getBalance(selectedAccount.financialAccount)
            .then(balance => {
                if (!cancelled) setCurrentBalance(balance);
            })
            .catch(() => {
                if (!cancelled) setCurrentBalance(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingBalance(false);
            });
        return () => { cancelled = true; };
    }, [selectedAccount?.financialAccount, refreshMovements]);

    // ── Handlers ──

    async function handleCreateAccount() {
        if (!newAccountName.trim()) {
            dispatch(setNotification({
                title: 'Nombre requerido',
                desc: 'Ingrese un nombre para la cuenta',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        setCreatingAccount(true);
        try {
            await createFinancialAccount({
                name: newAccountName.trim(),
                currency: newAccountCurrency,
                observations: newAccountObservations.trim() || undefined,
                financialAccountType: newAccountType,
                active: newAccountActive,
                paymentMethodIds: newAccountPaymentMethods.map(paymentMethod => paymentMethod.paymentMethodId),
            });
            dispatch(setNotification({
                title: 'Cuenta creada y configurada',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            setNewAccountModalVisible(false);
            resetNewAccountForm();
            setRefreshAccounts(v => v + 1);
        } catch (error: any) {
            dispatch(setNotification({
                title: 'No se pudo crear la cuenta',
                desc: error?.response?.data?.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setCreatingAccount(false);
        }
    }

    function resetNewAccountForm() {
        setNewAccountName('');
        setNewAccountCurrency('ARS');
        setNewAccountObservations('');
        setNewAccountType(2);
        setNewAccountActive(true);
        setNewAccountPaymentMethods([]);
    }

    async function openNewAccountModal() {
        resetNewAccountForm();
        setNewAccountModalVisible(true);
        if (allPaymentMethods.length > 0) return;

        setLoadingPMs(true);
        try {
            setAllPaymentMethods(await getAllPaymentMethods());
        } catch {
            dispatch(setNotification({
                title: 'No se pudieron cargar los medios de pago',
                desc: 'La cuenta podrá crearse sin restricciones de medios de pago.',
                icon: 'pi-exclamation-triangle',
                typeOfNotification: ETypeOfNotification.WARNING,
            }));
        } finally {
            setLoadingPMs(false);
        }
    }

    async function handleCreateMovement() {
        if (!selectedAccount) {
            dispatch(setNotification({
                title: 'Seleccione una cuenta',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        if (!movType) {
            dispatch(setNotification({
                title: 'Seleccione un tipo de movimiento',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        if (movAmount == null || (movType.id !== 4 && movAmount <= 0)) {
            dispatch(setNotification({
                title: 'Monto inválido',
                desc: movType.id === 4 ? 'Ingrese un monto (puede ser negativo para ajuste)' : 'El monto debe ser mayor a 0',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        setCreatingMovement(true);
        try {
            await createMovement({
                financialAccountId: selectedAccount.financialAccount,
                movementTypeId: movType.id,
                amount: movAmount,
                dateTime: dateParam(movDate),
                notes: movNotes.trim() || undefined,
                sourceEntity: 'MANUAL',
            });
            dispatch(setNotification({
                title: 'Movimiento registrado',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            setMovModalVisible(false);
            resetMovementForm();
            setRefreshMovements(v => v + 1);
        } catch (error) {
            const apiError = parseApiError(error);
            const amountError = apiError.errorCode === INSUFFICIENT_BALANCE
                ? getFieldErrorMessage(apiError, 'amount')
                : undefined;
            if (amountError) {
                setMovAmountError(amountError);
                movAmountRef.current?.focus();
                return;
            }
            dispatch(setNotification({
                title: 'No se pudo registrar el movimiento',
                desc: apiError.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setCreatingMovement(false);
        }
    }

    function resetMovementForm() {
        setMovType(null);
        setMovAmount(null);
        setMovAmountError(null);
        setMovDate(new Date());
        setMovNotes('');
    }

    function resetBalanceCorrectionForm() {
        setCorrectionAccount(null);
        setCorrectionCurrentBalance(null);
        setCorrectionTargetBalance(null);
        setCorrectionDate(new Date());
        setCorrectionNotes('');
        setCorrectionTargetError(null);
    }

    async function openBalanceCorrection(account: FinancialAccountDTO) {
        setCorrectionAccount(account);
        setCorrectionTargetError(null);
        setCorrectionNotes('');
        setCorrectionDate(new Date());
        setBalanceCorrectionVisible(true);

        const knownBalance = accountBalances[account.financialAccount];
        if (knownBalance != null) {
            setCorrectionCurrentBalance(knownBalance);
            setCorrectionTargetBalance(knownBalance);
            return;
        }

        setCorrectionCurrentBalance(null);
        setCorrectionTargetBalance(null);
        setLoadingCorrectionBalance(true);
        try {
            const balance = await getBalance(account.financialAccount);
            setCorrectionCurrentBalance(balance);
            setCorrectionTargetBalance(balance);
        } catch {
            dispatch(setNotification({
                title: 'No se pudo obtener el saldo actual',
                desc: 'Intente nuevamente antes de registrar la corrección.',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            setBalanceCorrectionVisible(false);
        } finally {
            setLoadingCorrectionBalance(false);
        }
    }

    async function handleCreateBalanceCorrection() {
        if (!correctionAccount || correctionTargetBalance == null || !Number.isFinite(correctionTargetBalance)) {
            dispatch(setNotification({
                title: 'Saldo objetivo requerido',
                desc: 'Ingrese un saldo objetivo válido.',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }

        if (correctionCurrentBalance != null && correctionTargetBalance === correctionCurrentBalance) {
            dispatch(setNotification({
                title: 'Sin cambios',
                desc: 'El saldo objetivo coincide con el saldo actual.',
                icon: 'pi-exclamation-triangle',
                typeOfNotification: ETypeOfNotification.WARNING,
            }));
            return;
        }

        setCorrectingBalance(true);
        try {
            await createBalanceCorrection(correctionAccount.financialAccount, {
                targetBalance: correctionTargetBalance,
                dateTime: dateParam(correctionDate),
                notes: correctionNotes.trim() || undefined,
            });
            dispatch(setNotification({
                title: 'Saldo corregido',
                desc: 'Se registró el movimiento de corrección de saldo.',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            setBalanceCorrectionVisible(false);
            resetBalanceCorrectionForm();
            setRefreshAccounts(v => v + 1);
            setRefreshMovements(v => v + 1);
        } catch (error) {
            const apiError = parseApiError(error);
            const targetError = apiError.errorCode === INSUFFICIENT_BALANCE
                ? getFieldErrorMessage(apiError, 'targetBalance')
                : undefined;
            if (targetError) {
                setCorrectionTargetError(targetError);
                correctionTargetRef.current?.focus();
                return;
            }
            dispatch(setNotification({
                title: 'No se pudo corregir el saldo',
                desc: apiError.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setCorrectingBalance(false);
        }
    }

    function openTransferModal() {
        setTransferDefaultSource(selectedAccount);
        setTransferModalVisible(true);
    }

    function handleTransferSuccess() {
        setRefreshMovements(v => v + 1);
        setRefreshAccounts(v => v + 1);
    }

    // ── Edit account ──

    async function handleOpenEdit(account: FinancialAccountDTO) {
        setEditingAccount(account);
        setEditName(account.name);
        setEditCurrency(account.currency);
        setEditObservations(account.observations ?? '');
        setEditActive(account.active);
        setEditModalVisible(true);

        // Load payment methods in parallel
        setLoadingPMs(true);
        try {
            const [allPMs, accountPMs] = await Promise.all([
                getAllPaymentMethods(),
                getAccountPaymentMethods(account.financialAccount),
            ]);
            setAllPaymentMethods(allPMs);
            setAccountPaymentMethods(accountPMs);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingPMs(false);
        }
    }

    async function handleSaveAccount() {
        if (!editingAccount || !editName.trim()) return;
        setSavingAccount(true);
        try {
            await updateFinancialAccount(editingAccount.financialAccount, {
                name: editName.trim(),
                currency: editCurrency,
                observations: editObservations.trim() || undefined,
                financialAccountType: editingAccount.financialAccountType,
                active: editActive,
            });
            dispatch(setNotification({
                title: 'Cuenta actualizada',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            setEditModalVisible(false);
            setRefreshAccounts(v => v + 1);
        } catch (error: any) {
            dispatch(setNotification({
                title: 'No se pudo actualizar la cuenta',
                desc: error?.response?.data?.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setSavingAccount(false);
        }
    }

    async function handlePaymentMethodsChange(e: MultiSelectChangeEvent) {
        if (!editingAccount) return;
        const newSelection: PaymentMethodDTO[] = e.value ?? [];
        const currentIds = new Set(accountPaymentMethods.map(pm => pm.paymentMethodId));
        const newIds = new Set(newSelection.map(pm => pm.paymentMethodId));

        // Asociar los nuevos
        for (const pm of newSelection) {
            if (!currentIds.has(pm.paymentMethodId)) {
                try {
                    await associatePaymentMethod(editingAccount.financialAccount, {
                        paymentMethodId: pm.paymentMethodId,
                    });
                } catch (error: any) {
                    dispatch(setNotification({
                        title: 'No se pudo asociar el medio de pago',
                        desc: error?.response?.data?.message,
                        icon: 'pi-times',
                        typeOfNotification: ETypeOfNotification.ERROR,
                    }));
                    return;
                }
            }
        }

        // Desasociar los removidos
        for (const pm of accountPaymentMethods) {
            if (!newIds.has(pm.paymentMethodId)) {
                try {
                    await disassociatePaymentMethod(editingAccount.financialAccount, pm.paymentMethodId);
                } catch (error: any) {
                    dispatch(setNotification({
                        title: 'No se pudo desasociar el medio de pago',
                        desc: error?.response?.data?.message,
                        icon: 'pi-times',
                        typeOfNotification: ETypeOfNotification.ERROR,
                    }));
                    return;
                }
            }
        }

        setAccountPaymentMethods(newSelection);
    }

    // ── Render ──

    return (
        <div className="financial-account-view viewContainer">
            <h1 className="fa-title">Cuentas financieras</h1>
            <p className="fa-subtitle">Gestión de cuentas y movimientos de tesorería.</p>

            {/* ── Tabs ── */}
            <div className="fa-tabs" role="tablist">
                <button
                    role="tab"
                    aria-selected={activeTab === 'accounts'}
                    className={`fa-tab ${activeTab === 'accounts' ? 'fa-tab--active' : ''}`}
                    onClick={() => setActiveTab('accounts')}
                >
                    Cuentas
                </button>
                <button
                    role="tab"
                    aria-selected={activeTab === 'movements'}
                    className={`fa-tab ${activeTab === 'movements' ? 'fa-tab--active' : ''}`}
                    onClick={() => setActiveTab('movements')}
                >
                    Movimientos
                </button>
            </div>

            {/* ══════════ TAB 1: CUENTAS ══════════ */}
            {activeTab === 'accounts' && (
                <>
                    <div className="fa-table-header">
                        <h3>Listado de cuentas</h3>
                        <Button
                            label="Nueva cuenta"
                            icon="pi pi-plus"
                            className="buttonMain"
                            onClick={() => {
                                void openNewAccountModal();
                            }}
                        />
                    </div>

                    <CustomTable
                        key={refreshAccounts}
                        idTable="tableFinancialAccounts"
                        getItems={() => getAllFinancialAccounts()}
                        filterRow={false}
                        hideFiltersButton
                        className="crudTable"
                    >
                        <Column
                            field="name"
                            header="Nombre"
                            style={{ minWidth: '180px' }}
                        />
                        <Column
                            field="financialAccountType"
                            header="Tipo"
                            body={(row: FinancialAccountDTO) => (
                                <span className={`fa-type-badge fa-type-badge--${row.financialAccountType === 1 ? 'bank' : 'cash'}`}>
                                    {ACCOUNT_TYPE_LABELS[row.financialAccountType] ?? row.financialAccountType}
                                </span>
                            )}
                            style={{ width: '100px' }}
                        />
                        <Column
                            field="currency"
                            header="Moneda"
                            style={{ width: '80px', textAlign: 'center' }}
                        />
                        <Column
                            header="Saldo actual"
                            body={(row: FinancialAccountDTO) => {
                                const balance = accountBalances[row.financialAccount];
                                if (balance == null) return <span style={{ color: '#999', fontSize: '12px' }}>Cargando...</span>;
                                return (
                                    <span className={balance >= 0 ? 'fa-amount--positive' : 'fa-amount--negative'}>
                                        {formatMoney(balance)}
                                    </span>
                                );
                            }}
                            style={{ width: '140px', textAlign: 'right' }}
                        />
                        <Column
                            field="active"
                            header="Activa"
                            body={(row: FinancialAccountDTO) => (
                                <span className={`fa-active-badge fa-active-badge--${row.active ? 'yes' : 'no'}`}>
                                    {row.active ? 'Sí' : 'No'}
                                </span>
                            )}
                            style={{ width: '80px', textAlign: 'center' }}
                        />
                            <Column
                                header=""
                                body={(row: FinancialAccountDTO) => (
                                <div className="fa-row-actions">
                                    <Button
                                        icon="pi pi-dollar"
                                        className="buttonMain3"
                                        tooltip="Corregir saldo"
                                        tooltipOptions={{ position: 'left' }}
                                        onClick={() => { void openBalanceCorrection(row); }}
                                        aria-label={`Corregir saldo de ${row.name}`}
                                        style={{ padding: '6px 10px' }}
                                    />
                                    <Button
                                        icon="pi pi-pencil"
                                        className="buttonMain3"
                                        tooltip="Editar cuenta"
                                        tooltipOptions={{ position: 'left' }}
                                        onClick={() => handleOpenEdit(row)}
                                        aria-label={`Editar ${row.name}`}
                                        style={{ padding: '6px 10px' }}
                                    />
                                </div>
                                )}
                                style={{ width: '60px', textAlign: 'center' }}
                            />
                    </CustomTable>

                    {/* ── Modal nueva cuenta ── */}
                    <Modal
                        visible={newAccountModalVisible}
                        setVisible={setNewAccountModalVisible}
                        header="Nueva cuenta financiera"
                        width="w-large"
                        closable={!creatingAccount}
                        className="financial-account-create-modal"
                    >
                        <div className="flex flex-column gap-3">
                            <div className="cc-form-grid">
                                <div className="cc-form-field">
                                    <InputTextCustom
                                        label="Nombre"
                                        required
                                        value={newAccountName}
                                        onChange={(e: any) => setNewAccountName(e.target.value)}
                                        placeholder="Ej: Caja central, Banco Nación"
                                        disable={creatingAccount}
                                    />
                                </div>
                                <div className="cc-form-field">
                                    <Combobox
                                        label="Moneda"
                                        list={CURRENCY_OPTIONS.map(c => ({ id: c.value, name: c.label, displayDescription: c.label }))}
                                        value={newAccountCurrency ? { id: newAccountCurrency, name: newAccountCurrency, displayDescription: newAccountCurrency } : null}
                                        setValue={(v: any) => setNewAccountCurrency(v?.id ?? 'ARS')}
                                        optionLabel="displayDescription"
                                        placeholder="Seleccionar..."
                                    />
                                </div>
                                <div className="cc-form-field">
                                    <Combobox
                                        label="Tipo"
                                        list={[
                                            { id: 1, name: 'Bancaria', displayDescription: 'Bancaria' },
                                            { id: 2, name: 'Física', displayDescription: 'Física' },
                                        ]}
                                        value={{ id: newAccountType, name: ACCOUNT_TYPE_LABELS[newAccountType], displayDescription: ACCOUNT_TYPE_LABELS[newAccountType] }}
                                        setValue={(v: any) => setNewAccountType(v?.id ?? 2)}
                                        optionLabel="displayDescription"
                                        placeholder="Seleccionar..."
                                    />
                                </div>
                                <div className="cc-form-field">
                                    <Combobox
                                        label="Estado"
                                        list={[
                                            { id: true, name: 'Activa', displayDescription: 'Activa' },
                                            { id: false, name: 'Inactiva', displayDescription: 'Inactiva' },
                                        ]}
                                        value={{ id: newAccountActive, name: newAccountActive ? 'Activa' : 'Inactiva', displayDescription: newAccountActive ? 'Activa' : 'Inactiva' }}
                                        setValue={(v: any) => setNewAccountActive(v?.id ?? true)}
                                        optionLabel="displayDescription"
                                        placeholder="Seleccionar..."
                                    />
                                </div>
                            </div>
                            <div className="cc-form-field">
                                <label>Observaciones</label>
                                <InputTextarea
                                    value={newAccountObservations}
                                    onChange={e => setNewAccountObservations(e.target.value)}
                                    rows={2}
                                    autoResize
                                    placeholder="Opcional"
                                    disabled={creatingAccount}
                                />
                            </div>
                            <div className="cc-form-field fa-create-payment-methods">
                                <label>Medios de pago admitidos</label>
                                {loadingPMs ? (
                                    <p className="fa-inline-loading">Cargando medios de pago...</p>
                                ) : (
                                    <MultiSelect
                                        value={newAccountPaymentMethods}
                                        options={allPaymentMethods}
                                        onChange={(event: MultiSelectChangeEvent) => setNewAccountPaymentMethods(event.value ?? [])}
                                        optionLabel="description"
                                        dataKey="paymentMethodId"
                                        placeholder="Sin restricción (acepta todos)"
                                        display="chip"
                                        disabled={creatingAccount}
                                        className="w-full"
                                    />
                                )}
                                <span className="fa-field-hint"><i className="pi pi-info-circle" aria-hidden="true" /> Puede dejarse vacío para aceptar todos los medios de pago.</span>
                            </div>
                            <div className="flex justify-content-end gap-2 mt-2">
                                <Button
                                    label="Cancelar"
                                    className="buttonMain3"
                                    onClick={() => setNewAccountModalVisible(false)}
                                    disabled={creatingAccount}
                                />
                                <Button
                                    label="Crear cuenta"
                                    icon="pi pi-check"
                                    className="buttonMain"
                                    onClick={handleCreateAccount}
                                    loading={creatingAccount}
                                    disabled={!newAccountName.trim()}
                                />
                            </div>
                        </div>
                    </Modal>

                    {/* ── Modal corrección de saldo ── */}
                    <Modal
                        visible={balanceCorrectionVisible}
                        setVisible={(visible) => {
                            setBalanceCorrectionVisible(visible);
                            if (!visible) resetBalanceCorrectionForm();
                        }}
                        header="Corregir saldo de cuenta"
                        width="w-medium"
                        closable={!correctingBalance}
                        className="financial-account-modal"
                    >
                        <div className="fa-balance-correction-form">
                            <div className="fa-balance-correction-intro">
                                <i className="pi pi-info-circle" aria-hidden="true" />
                                <p>Se registrará un movimiento de tipo “Corrección de saldo de cuenta”. El saldo histórico no se modifica.</p>
                            </div>
                            <div className="fa-balance-correction-account">
                                <span>Cuenta</span>
                                <strong>{correctionAccount?.name ?? '—'}</strong>
                            </div>
                            <div className="fa-balance-correction-grid">
                                <div className="fa-balance-correction-field">
                                    <label>Saldo actual</label>
                                    <div className="fa-balance-correction-readonly">
                                        {loadingCorrectionBalance ? 'Cargando...' : formatMoney(correctionCurrentBalance)}
                                    </div>
                                </div>
                                <div className="fa-balance-correction-field">
                                    <label htmlFor="balance-correction-target">Saldo objetivo *</label>
                                    <InputNumber
                                        inputId="balance-correction-target"
                                        inputRef={correctionTargetRef}
                                        value={correctionTargetBalance}
                                        onValueChange={event => {
                                            setCorrectionTargetBalance(event.value ?? null);
                                            setCorrectionTargetError(null);
                                        }}
                                        mode="currency"
                                        currency={correctionAccount?.currency ?? 'ARS'}
                                        locale="es-AR"
                                        disabled={loadingCorrectionBalance || correctingBalance}
                                        className={correctionTargetError ? 'field-error-target' : undefined}
                                        aria-invalid={correctionTargetError ? true : undefined}
                                        aria-describedby={correctionTargetError ? 'balance-correction-target-error' : undefined}
                                    />
                                    <FieldError id="balance-correction-target-error" message={correctionTargetError} />
                                </div>
                            </div>
                            {correctionCurrentBalance != null && correctionTargetBalance != null && correctionTargetBalance !== correctionCurrentBalance && (
                                <div className="fa-balance-correction-difference">
                                    <span>Diferencia a registrar</span>
                                    <strong className={correctionTargetBalance > correctionCurrentBalance ? 'fa-amount--positive' : 'fa-amount--negative'}>
                                        {formatMoney(correctionTargetBalance - correctionCurrentBalance)}
                                    </strong>
                                </div>
                            )}
                            <div className="fa-balance-correction-field">
                                <InputDate label="Fecha" value={correctionDate} onChange={(event: any) => setCorrectionDate(event.value ?? new Date())} />
                            </div>
                            <div className="fa-balance-correction-field">
                                <label htmlFor="balance-correction-notes">Observaciones</label>
                                <InputTextarea id="balance-correction-notes" value={correctionNotes} onChange={event => setCorrectionNotes(event.target.value)} rows={3} autoResize placeholder="Indique el motivo de la corrección" disabled={correctingBalance} />
                            </div>
                            <div className="fa-modal-actions">
                                <Button label="Cancelar" className="buttonMain3" onClick={() => setBalanceCorrectionVisible(false)} disabled={correctingBalance} />
                                <Button label="Registrar corrección" icon="pi pi-check" className="buttonMain" onClick={handleCreateBalanceCorrection} loading={correctingBalance} disabled={loadingCorrectionBalance || correctionTargetBalance == null} />
                            </div>
                        </div>
                    </Modal>

                    {/* ── Modal editar cuenta ── */}
                    <Modal
                        visible={editModalVisible}
                        setVisible={setEditModalVisible}
                        header={`Editar cuenta — ${editingAccount?.name ?? ''}`}
                        width="w-large"
                        closable={!savingAccount}
                    >
                        {editingAccount && (
                            <div className="flex flex-column gap-3">
                                {/* Datos de la cuenta */}
                                <div className="cc-form-grid">
                                    <div className="cc-form-field">
                                        <InputTextCustom
                                            label="Nombre"
                                            required
                                            value={editName}
                                            onChange={(e: any) => setEditName(e.target.value)}
                                            disable={savingAccount}
                                        />
                                    </div>
                                    <div className="cc-form-field">
                                        <Combobox
                                            label="Moneda"
                                            list={CURRENCY_OPTIONS.map(c => ({ id: c.value, name: c.label, displayDescription: c.label }))}
                                            value={editCurrency ? { id: editCurrency, name: editCurrency, displayDescription: editCurrency } : null}
                                            setValue={(v: any) => setEditCurrency(v?.id ?? 'ARS')}
                                            optionLabel="displayDescription"
                                            placeholder="Seleccionar..."
                                        />
                                    </div>
                                    <div className="cc-form-field">
                                        <Combobox
                                            label="Activa"
                                            list={[
                                                { id: true, name: 'Sí', displayDescription: 'Sí' },
                                                { id: false, name: 'No', displayDescription: 'No' },
                                            ]}
                                            value={editActive != null ? { id: editActive, name: editActive ? 'Sí' : 'No', displayDescription: editActive ? 'Sí' : 'No' } : null}
                                            setValue={(v: any) => setEditActive(v?.id ?? true)}
                                            optionLabel="displayDescription"
                                            placeholder="Seleccionar..."
                                        />
                                    </div>
                                </div>
                                <div className="cc-form-field">
                                    <label>Observaciones</label>
                                    <InputTextarea
                                        value={editObservations}
                                        onChange={e => setEditObservations(e.target.value)}
                                        rows={2}
                                        autoResize
                                        disabled={savingAccount}
                                    />
                                </div>

                                {/* Medios de pago */}
                                <div className="cc-form-field">
                                    <label>Medios de pago admitidos</label>
                                    {loadingPMs ? (
                                        <p style={{ color: '#999', fontSize: '13px' }}>Cargando...</p>
                                    ) : (
                                        <MultiSelect
                                            value={accountPaymentMethods}
                                            options={allPaymentMethods}
                                            onChange={handlePaymentMethodsChange}
                                            optionLabel="description"
                                            dataKey="paymentMethodId"
                                            placeholder="Sin restricción (acepta todos)"
                                            display="chip"
                                            disabled={savingAccount}
                                            className="w-full"
                                        />
                                    )}
                                    <span style={{ fontSize: '11px', color: '#5A6676', marginTop: '2px' }}>
                                        <i className="pi pi-info-circle" style={{ fontSize: '11px', marginRight: '4px' }} />
                                        Sin medios seleccionados, la cuenta acepta todos.
                                    </span>
                                </div>

                                <div className="flex justify-content-end gap-2 mt-2">
                                    <Button
                                        label="Cancelar"
                                        className="buttonMain3"
                                        onClick={() => setEditModalVisible(false)}
                                        disabled={savingAccount}
                                    />
                                    <Button
                                        label="Guardar cambios"
                                        icon="pi pi-check"
                                        className="buttonMain"
                                        onClick={handleSaveAccount}
                                        loading={savingAccount}
                                        disabled={!editName.trim()}
                                    />
                                </div>
                            </div>
                        )}
                    </Modal>
                </>
            )}

            {/* ══════════ TAB 2: MOVIMIENTOS ══════════ */}
            {activeTab === 'movements' && (
                <>
                    {/* Selector de cuenta + filtros de fecha */}
                    <div className="fa-form">
                        <div className="flex align-items-end gap-4 flex-wrap">
                            <div style={{ minWidth: '240px', flex: 1 }}>
                                <Combobox
                                    label="Cuenta *"
                                    list={accountOptions}
                                    value={selectedAccount}
                                    setValue={(v: AccountOption | null) => setSelectedAccount(v)}
                                    optionLabel="displayDescription"
                                    placeholder="Seleccionar cuenta..."
                                />
                            </div>
                            <div style={{ width: '180px' }}>
                                <InputDate
                                    label="Desde"
                                    value={movFromDate}
                                    onChange={(e: any) => setMovFromDate(e.value ?? startOfDay(new Date()))}
                                />
                            </div>
                            <div style={{ width: '180px' }}>
                                <InputDate
                                    label="Hasta"
                                    value={movToDate}
                                    onChange={(e: any) => setMovToDate(e.value ?? new Date())}
                                />
                            </div>
                        </div>
                    </div>

                    {selectedAccount && (
                        <>
                            {/* Balance card */}
                            <div className="fa-balance-card">
                                <span className="fa-balance-label">Saldo actual:</span>
                                {loadingBalance ? (
                                    <span style={{ color: '#999', fontSize: '13px' }}>Cargando...</span>
                                ) : (
                                    <span className={`fa-balance-value ${
                                        currentBalance == null ? '' :
                                        currentBalance >= 0 ? 'fa-balance-value--positive' : 'fa-balance-value--negative'
                                    }`}>
                                        {formatMoney(currentBalance)}
                                    </span>
                                )}
                            </div>

                            {/* Acciones */}
                            <div className="fa-table-header">
                                <h3>Movimientos</h3>
                                <div className="fa-table-actions">
                                    <Button
                                        label="Nuevo movimiento"
                                        icon="pi pi-plus"
                                        className="buttonMain"
                                        onClick={() => {
                                            resetMovementForm();
                                            setMovModalVisible(true);
                                        }}
                                    />
                                    <Button
                                        label="Transferencia entre cuentas"
                                        icon="pi pi-arrow-right-arrow-left"
                                        className="buttonMain3"
                                        onClick={openTransferModal}
                                    />
                                </div>
                            </div>

                            {/* Tabla de movimientos */}
                            <CustomTable
                                key={`${refreshMovements}-${selectedAccount.financialAccount}-${movFromDate.getTime()}-${movToDate.getTime()}`}
                                idTable="tableMovements"
                                getItems={(offset: number, pageSize: number) =>
                                    listMovements({
                                        financialAccountId: selectedAccount.financialAccount,
                                        from: dateParam(startOfDay(movFromDate)),
                                        to: dateParam(endOfDay(movToDate)),
                                        offset,
                                        limit: pageSize,
                                    }).then(res => res.items)
                                }
                                filterRow={false}
                                hideFiltersButton
                                className="crudTable"
                            >
                                <Column
                                    field="dateTime"
                                    header="Fecha/hora"
                                    body={(row: MovementResponseDTO) => formatDate(row.dateTime)}
                                    style={{ width: '160px' }}
                                />
                                <Column
                                    field="movementTypeDescription"
                                    header="Tipo"
                                    style={{ width: '180px' }}
                                />
                                <Column
                                    field="counterpartAccountName"
                                    header="Contraparte"
                                    body={formatCounterpart}
                                    style={{ width: '200px' }}
                                />
                                <Column
                                    field="amount"
                                    header="Monto"
                                    body={(row: MovementResponseDTO) => (
                                        <span className={row.amount >= 0 ? 'fa-amount--positive' : 'fa-amount--negative'}>
                                            {formatMoney(row.amount)}
                                        </span>
                                    )}
                                    style={{ width: '130px', textAlign: 'right' }}
                                />
                                <Column
                                    field="balance"
                                    header="Saldo"
                                    body={(row: MovementResponseDTO) => formatMoney(row.balance)}
                                    style={{ width: '130px', textAlign: 'right' }}
                                />
                                <Column
                                    field="userFullName"
                                    header="Usuario"
                                    body={(row: MovementResponseDTO) => row.userFullName || '—'}
                                    style={{ width: '180px' }}
                                />
                                <Column
                                    field="notes"
                                    header="Notas"
                                    body={(row: MovementResponseDTO) => row.notes ?? '—'}
                                />
                            </CustomTable>
                        </>
                    )}

                    {/* ── Modal nuevo movimiento ── */}
                    <Modal
                        visible={movModalVisible}
                        setVisible={setMovModalVisible}
                        header="Nuevo movimiento"
                        width="w-medium"
                        closable={!creatingMovement}
                    >
                        <div className="flex flex-column gap-3">
                            <div className="cc-form-field">
                                <Combobox
                                    label="Tipo de movimiento *"
                                    list={movementTypes}
                                    value={movType}
                                    setValue={(v: MovementTypeOption | null) => setMovType(v)}
                                    optionLabel="description"
                                    placeholder="Seleccionar tipo..."
                                />
                            </div>
                            <div className="cc-form-grid">
                                <div className="cc-form-field">
                                    <label htmlFor="movement-amount">Monto *</label>
                                    <InputNumber
                                        inputId="movement-amount"
                                        inputRef={movAmountRef}
                                        value={movAmount}
                                        onValueChange={e => {
                                            setMovAmount(e.value ?? null);
                                            setMovAmountError(null);
                                        }}
                                        mode="currency"
                                        currency="ARS"
                                        locale="es-AR"
                                        min={movType?.id === 4 ? -999999999 : 0}
                                        disabled={creatingMovement || !movType}
                                        className={movAmountError ? 'field-error-target' : undefined}
                                        aria-invalid={movAmountError ? true : undefined}
                                        aria-describedby={movAmountError ? 'movement-amount-error' : undefined}
                                    />
                                    <FieldError id="movement-amount-error" message={movAmountError} />
                                    {movType?.id === 4 && !movAmountError && (
                                        <span style={{ fontSize: '11px', color: '#5A6676', marginTop: '2px' }}>
                                            <i className="pi pi-info-circle" style={{ fontSize: '11px', marginRight: '4px' }} />
                                            Ajuste: permite monto negativo
                                        </span>
                                    )}
                                </div>
                                <div className="cc-form-field">
                                    <InputDate
                                        label="Fecha"
                                        value={movDate}
                                        onChange={(e: any) => setMovDate(e.value ?? new Date())}
                                    />
                                </div>
                            </div>
                            <div className="cc-form-field">
                                <label>Notas</label>
                                <InputTextarea
                                    value={movNotes}
                                    onChange={e => setMovNotes(e.target.value)}
                                    rows={2}
                                    autoResize
                                    placeholder="Opcional"
                                    disabled={creatingMovement}
                                />
                            </div>
                            <div className="flex justify-content-end gap-2 mt-2">
                                <Button
                                    label="Cancelar"
                                    className="buttonMain3"
                                    onClick={() => setMovModalVisible(false)}
                                    disabled={creatingMovement}
                                />
                                <Button
                                    label="Registrar movimiento"
                                    icon="pi pi-check"
                                    className="buttonMain"
                                    onClick={handleCreateMovement}
                                    loading={creatingMovement}
                                    disabled={!movType || movAmount == null}
                                />
                            </div>
                        </div>
                    </Modal>

                    {/* ── Modal transferencia ── */}
                    <TransferModal
                        visible={transferModalVisible}
                        onHide={() => setTransferModalVisible(false)}
                        sourceAccounts={accountOptions}
                        destinationAccounts={accountOptions}
                        defaultSource={transferDefaultSource}
                        onSuccess={handleTransferSuccess}
                    />
                </>
            )}
        </div>
    );
}
